"use client";

import { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import { certificationsData } from "@/data/certifications";

gsap.registerPlugin(useGSAP);

export const CertificationsMarquee = () => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const tween = useRef<gsap.core.Tween | null>(null);

  useGSAP(
    () => {
      if (!marqueeRef.current) return;

      // With 4 identical blocks, shifting -50% advances through exactly 2 full sets
      // before resetting seamlessly back to 0 without any empty gaps.
      tween.current = gsap.to(marqueeRef.current, {
        xPercent: -50,
        duration: 30,
        ease: "none",
        repeat: -1,
      });
    },
    { scope: marqueeRef }
  );

  // Helper block to render a set of items consistently
  const renderItemSet = (setId: string, isAriaHidden = false) => (
    <div
      aria-hidden={isAriaHidden ? "true" : undefined}
      className="flex shrink-0 items-center gap-16 pr-16 lg:gap-24 lg:pr-24"
    >
      {certificationsData.map((item, index) => (
        <div
          key={`cert-${setId}-${item.id || index}`}
          className="group flex h-20 items-center justify-center shrink-0"
        >
          <Image
            src={item.imagePath}
            alt={item.name}
            width={180}
            height={70}
            className="h-12 w-auto object-contain opacity-80 transition-all duration-300 group-hover:scale-105 group-hover:opacity-100"
          />
        </div>
      ))}
    </div>
  );

  return (
    <section className="w-full overflow-hidden border-y border-neutral-200 bg-white py-8">
      <div className="mx-auto max-w-[1500px] px-6">
        <p className="mb-8 text-xs font-bold uppercase tracking-[0.35em] text-neutral-500">
          OUR CERTIFICATIONS
        </p>

        <div
          className="relative overflow-hidden"
          onMouseEnter={() => tween.current?.pause()}
          onMouseLeave={() => tween.current?.resume()}
        >
          {/* Gradient Edge Masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />

          {/* Marquee Track (4 identical sets for infinite loop coverage) */}
          <div ref={marqueeRef} className="flex w-max items-center">
            {renderItemSet("1")}
            {renderItemSet("2", true)}
            {renderItemSet("3", true)}
            {renderItemSet("4", true)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationsMarquee;